import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./TerrorImport.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

const API_BASE = "https://www.lianfoong.com/api/terror-list";
const API_REPLACE = `${API_BASE}/replace`;
const API_RESCAN = `${API_BASE}/rescan`;
const API_STATUS = `${API_BASE}/status`;
const API_DAILY = `${API_BASE}/daily-screening`;
const pepLabels = {
  NO_MATCH_FOUND: "Cleared", POSSIBLE_MATCH: "Possible PEP match",
};

const singaporeToday = () => {
  const parts = new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const part = (type) => parts.find((item) => item.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};

const screeningLabels = {
  CLEARED: "Cleared", EXACT_MATCH: "Name + birthdate match",
  NAME_ONLY_MATCH: "Name-only match", POSSIBLE_MATCH: "Potential issue / review", INCOMPLETE: "Details unavailable", NOT_CHECKED: "List not available",
};

const displayTimestamp = (value) => {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).replace("T", " ");
  return d.toLocaleString("en-SG", {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const displayDate = (value) => {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return String(value);
};

const text = (value) => (value === null || value === undefined || value === "" ? "—" : String(value));

const escapeHtml = (value) =>
  text(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export default function TerrorImport() {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultKind, setResultKind] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState({ lastUpdatedAt: null, lastScanAt: null, activeCount: null });
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [printTime, setPrintTime] = useState(null);
  const [screeningDate, setScreeningDate] = useState(singaporeToday);
  const [dailyScreening, setDailyScreening] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [dailyError, setDailyError] = useState("");
  const [dailyRefresh, setDailyRefresh] = useState(0);

  useEscapeToGoBack();

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError("");
    try {
      const res = await fetch(API_STATUS, { cache: "no-store" });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Unable to load status (${res.status})`);
      setStatus({
        lastUpdatedAt: json.lastUpdatedAt || null,
        lastScanAt: json.lastScanAt || null,
        activeCount: json.activeCount ?? null,
      });
    } catch (e) {
      setStatusError(e.message || "Unable to load terrorist list status.");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const onFocus = () => loadStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadStatus]);

  useEffect(() => {
    const controller = new AbortController();
    setDailyLoading(true);
    setDailyScreening(null);
    setDailyError("");
    const load = async () => {
      try {
        const res = await fetch(`${API_DAILY}?date=${encodeURIComponent(screeningDate)}`, {
          cache: "no-store", signal: controller.signal,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || `Unable to load daily screening (${res.status}). Please deploy the matching backend update.`);
        if (!Array.isArray(json.content)) throw new Error("Unexpected daily screening response.");
        if (!controller.signal.aborted) setDailyScreening(json);
      } catch (e) {
        if (!controller.signal.aborted) setDailyError(e.message || "Unable to load daily screening.");
      } finally {
        if (!controller.signal.aborted) setDailyLoading(false);
      }
    };
    void load();
    const onFocus = () => setDailyRefresh((value) => value + 1);
    window.addEventListener("focus", onFocus);
    return () => {
      controller.abort();
      window.removeEventListener("focus", onFocus);
    };
  }, [screeningDate, dailyRefresh]);

  const onFilePick = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") {
      setError("Please select a PDF file.");
      return;
    }
    setError("");
    setResult(null);
    setResultKind(null);
    setPrintTime(null);
    setFile(f);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    onFilePick(f);
  }, []);

  const onUpload = async () => {
    if (!file) {
      setError("Select a PDF first.");
      return;
    }

    setError("");
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(API_REPLACE, { method: "POST", body: form });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Import failed (${res.status})`);

      setResult(json);
      setResultKind("upload");
      setDailyRefresh((value) => value + 1);
      setPrintTime(null);
      setStatus((prev) => ({
        ...prev,
        lastUpdatedAt: json.lastUpdatedAt || prev.lastUpdatedAt,
        lastScanAt: json.lastScanAt || prev.lastScanAt,
        activeCount: json.newActive ?? prev.activeCount,
      }));
    } catch (e) {
      setError(e.message || "Import failed.");
    } finally {
      setUploading(false);
      setConfirming(false);
    }
  };

  const onRescan = async () => {
    setError("");
    setStatusError("");
    setRescanning(true);
    setResult(null);
    setResultKind(null);
    setPrintTime(null);

    try {
      const res = await fetch(API_RESCAN, { method: "POST" });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Rescan failed (${res.status})`);

      setResult(json);
      setResultKind("rescan");
      setDailyRefresh((value) => value + 1);
      setStatus((prev) => ({
        ...prev,
        lastUpdatedAt: json.lastUpdatedAt || prev.lastUpdatedAt,
        lastScanAt: json.lastScanAt || prev.lastScanAt,
        activeCount: json.activeCount ?? prev.activeCount,
      }));
    } catch (e) {
      setError(e.message || "Rescan failed.");
    } finally {
      setRescanning(false);
    }
  };

  const ticketList = (match) => {
    if (Array.isArray(match?.activeTickets) && match.activeTickets.length > 0) {
      return match.activeTickets;
    }
    return [];
  };

  const matches = Array.isArray(result?.matches) ? result.matches : [];
  const matchCount = Number(result?.customerMatchCount ?? 0);

  const printMatches = () => {
    if (matches.length === 0) return;

    const printedAt = new Date().toISOString();
    setPrintTime(printedAt);

    const rowsHtml = matches.map((m) => `
      <tr>
        <td class="match-type ${m.dobMatch ? "strong" : ""}">${escapeHtml(m.matchType)}</td>
        <td>${escapeHtml(m.customerNric)}</td>
        <td>${escapeHtml(m.customerName)}</td>
        <td>${escapeHtml(displayDate(m.customerDob))}</td>
        <td>${escapeHtml(m.customerMobile)}</td>
        <td class="tickets">${ticketList(m).map((ticket) => escapeHtml(ticket)).join("<br />") || "—"}</td>
        <td>${escapeHtml(m.terroristName)}</td>
        <td>${escapeHtml(displayDate(m.terroristDob))}</td>
        <td>${escapeHtml(m.terroristPassportNo)}</td>
        <td>${escapeHtml(m.terroristNationality)}</td>
      </tr>
    `).join("");

    // Print from a clean document instead of the full React/app layout.
    // This prevents parent dashboard/sidebar CSS from shrinking or shifting the report.
    const frame = document.createElement("iframe");
    frame.setAttribute("title", "Terrorist List Match Report");
    frame.style.position = "fixed";
    frame.style.left = "-10000px";
    frame.style.top = "0";
    frame.style.width = "1px";
    frame.style.height = "1px";
    frame.style.border = "0";
    document.body.appendChild(frame);

    const printWindow = frame.contentWindow;
    const printDocument = printWindow?.document;

    if (!printWindow || !printDocument) {
      frame.remove();
      setError("Unable to open the print report. Please try again.");
      return;
    }

    printDocument.open();
    printDocument.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Terrorist List - Active Ticket Customer Matches</title>
  <style>
    @page {
      size: A4 landscape;
      margin: 10mm;
    }

    * { box-sizing: border-box; }

    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      background: #fff;
      color: #111827;
      font-family: Arial, Helvetica, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .report {
      width: 100%;
      margin: 0;
      padding: 0;
    }

    h1 {
      margin: 0 0 4px;
      font-size: 17px;
      line-height: 1.2;
    }

    .meta {
      margin: 0 0 8px;
      font-size: 9px;
      color: #374151;
    }

    .note {
      margin: 0 0 9px;
      padding: 5px 7px;
      border: 1px solid #9ca3af;
      border-radius: 3px;
      font-size: 8.5px;
      line-height: 1.35;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 8px;
    }

    col.match { width: 7%; }
    col.customer-id { width: 11%; }
    col.customer-name { width: 14%; }
    col.customer-dob { width: 8%; }
    col.mobile { width: 9%; }
    col.tickets { width: 14%; }
    col.listed-name { width: 14%; }
    col.listed-dob { width: 8%; }
    col.passport { width: 8%; }
    col.nationality { width: 7%; }

    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    th, td {
      border: 1px solid #6b7280;
      padding: 3.5px 4px;
      text-align: left;
      vertical-align: top;
      overflow-wrap: anywhere;
      word-break: normal;
    }

    th {
      background: #e5e7eb;
      font-weight: 700;
      font-size: 7.5px;
      white-space: normal;
    }

    .match-type {
      font-weight: 700;
      color: #9a3412;
    }

    .match-type.strong {
      color: #991b1b;
    }
  </style>
</head>
<body>
  <main class="report">
    <h1>Terrorist List — Active Ticket Customer Matches</h1>
    <p class="meta">
      List updated: <strong>${escapeHtml(displayTimestamp(result?.lastUpdatedAt || status.lastUpdatedAt))}</strong>
      &nbsp;·&nbsp; Printed: <strong>${escapeHtml(displayTimestamp(printedAt))}</strong>
      &nbsp;·&nbsp; Active-ticket customers matched: <strong>${matchCount}</strong>
    </p>
    <div class="note">
      Only customers with current active, unredeemed tickets are included. Exact normalized name matching is used. <strong>NAME ONLY</strong> is included even when DOB differs or is unavailable; <strong>NAME + DOB</strong> means both fields matched.
    </div>

    <table>
      <colgroup>
        <col class="match" />
        <col class="customer-id" />
        <col class="customer-name" />
        <col class="customer-dob" />
        <col class="mobile" />
        <col class="tickets" />
        <col class="listed-name" />
        <col class="listed-dob" />
        <col class="passport" />
        <col class="nationality" />
      </colgroup>
      <thead>
        <tr>
          <th>Match</th>
          <th>Customer NRIC / ID</th>
          <th>Customer Name</th>
          <th>Customer DOB</th>
          <th>Mobile</th>
          <th>Active Ticket No(s)</th>
          <th>Listed Name</th>
          <th>Listed DOB</th>
          <th>Passport</th>
          <th>Nationality</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  </main>
</body>
</html>`);
    printDocument.close();

    const cleanup = () => {
      window.setTimeout(() => frame.remove(), 250);
    };

    printWindow.addEventListener("afterprint", cleanup, { once: true });

    // A short delay lets Chrome/Firefox finish laying out the A4 print document.
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 150);
  };

  return (
    <div className="terror-import">
      <div className="ti-page">
        <div className="ti-header ti-screen-only">
          <button className="ti-btn ti-btn--ghost" onClick={() => navigate(-1)}>← Back</button>
          <h2 className="ti-title">Terrorist List</h2>
        </div>

        <div className="ti-status-grid ti-screen-only">
          <div className="ti-status-card">
            <span className="ti-status-label">Last Updated</span>
            <strong>{statusLoading && !status.lastUpdatedAt ? "Loading…" : displayTimestamp(status.lastUpdatedAt)}</strong>
            <small>Last successful terrorist-list upload</small>
            <button className="ti-link-btn" type="button" onClick={loadStatus} disabled={statusLoading}>
              {statusLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {statusError && <div className="ti-alert ti-alert--error ti-screen-only">{statusError}</div>}

        <section className="ti-card ti-daily-card ti-screen-only" aria-labelledby="daily-screening-heading">
          <div className="ti-daily-header">
            <div>
              <h3 id="daily-screening-heading">Daily Screening — ROP and PEP</h3>
              <p>People with grants, renewals or redemptions on the selected date, including third-party redeemers. Each person appears once with their tickets.</p>
            </div>
            <div className="ti-daily-controls">
              <label className="ti-date-label">
                Transaction date
                <span className="ti-date-picker">
                  <span aria-hidden="true">{displayDate(screeningDate)} <span className="ti-calendar-icon">▦</span></span>
                  <input type="date" value={screeningDate} aria-label="Transaction date"
                    onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* Native picker fallback */ } }}
                    onChange={(e) => { if (e.target.value) setScreeningDate(e.target.value); }} />
                </span>
              </label>
              <button className="ti-btn ti-btn--ghost" type="button" onClick={() => setScreeningDate(singaporeToday())}>Today</button>
              <button className="ti-btn ti-btn--rescan" type="button" disabled={dailyLoading || uploading || rescanning}
                onClick={() => setDailyRefresh((value) => value + 1)}>Refresh Names</button>
            </div>
          </div>
          <p className="ti-match-note">Cleared applies to the named column: the current uploaded ROP terrorist list, or a completed PEP search with no potential match. PEP is shown separately and does not change ROP clearance. PEP shows available results from the past 24 hours. This is a current screening view, not proof of checks performed on the transaction date.</p>
          {dailyError && <div className="ti-alert ti-alert--error" role="alert">{dailyError}</div>}
          {dailyLoading && <p role="status">Loading names…</p>}
          {!dailyLoading && dailyScreening && (
            <>
              <div className="ti-daily-counts" role="status">
                <span>{dailyScreening.customerCount} person{dailyScreening.customerCount === 1 ? "" : "s"}</span>
                <span className="ti-cleared-count">{dailyScreening.clearedCount} ROP cleared</span>
                <span>{dailyScreening.reviewCount} ROP review / unavailable</span>
              </div>
              {!dailyScreening.listAvailable && <p className="ti-match-note">Upload a terrorist list to view ROP screening results.</p>}
              {dailyScreening.content.length === 0 ? <p>No pawn transactions found for {displayDate(screeningDate)}.</p> : (
                <div className="ti-table-wrap">
                  <table className="ti-daily-table">
                    <thead><tr><th>Customer Name</th><th>NRIC / ID</th><th>Birthdate</th><th>Transaction</th><th>Ticket No(s)</th><th>Terrorist (ROP)</th><th>PEP</th></tr></thead>
                    <tbody>{dailyScreening.content.map((row) => (
                      <tr key={row.key}>
                        <td>{row.customerName || "Customer details missing"}</td>
                        <td>{text(row.customerNric)}</td><td>{displayDate(row.customerDob)}</td>
                        <td>{row.sources.join(", ")}</td>
                        <td>{row.tickets.map((ticket) => <div key={ticket}>{ticket}</div>)}</td>
                        <td>{["CLEARED", "EXACT_MATCH", "NAME_ONLY_MATCH", "POSSIBLE_MATCH"].includes(row.result) && <><span className={`ti-screening-badge ${row.result === "CLEARED" ? "ti-screening-badge--clear" : "ti-screening-badge--review"}`}>
                          {screeningLabels[row.result] || row.result}</span>
                          {row.note && <small className="ti-screening-note">{row.note}</small>}</>}
                          {row.onlineTerrorismStatus === "POSSIBLE_MATCH" && <small className="ti-screening-badge ti-screening-badge--review">Online terrorist match — review</small>}</td>
                        <td>{["NO_MATCH_FOUND", "POSSIBLE_MATCH"].includes(row.pepResult) && <><span className={`ti-screening-badge ${row.pepResult === "NO_MATCH_FOUND" ? "ti-screening-badge--clear" : "ti-screening-badge--review"}`}>
                          {pepLabels[row.pepResult]}</span>
                          {row.pepResult === "POSSIBLE_MATCH" && row.pepNote && <small className="ti-screening-note">{row.pepNote}</small>}
                          {row.pepCheckedAt && <small className="ti-screening-note">{displayTimestamp(row.pepCheckedAt)} SGT</small>}</>}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        <div
          className={`ti-dropzone ti-screen-only ${dragOver ? "ti-drag-over" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
          }}
        >
          {file ? (
            <>
              <p><strong>Selected:</strong> {file.name}</p>
              <p>Size: {(file.size / (1024 * 1024)).toFixed(2)} MB</p>
            </>
          ) : (
            <>
              <p>Drag & drop the latest PDF here</p>
              <p>or click to choose a file</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => onFilePick(e.target.files?.[0])}
            hidden
          />
        </div>

        {error && <div className="ti-alert ti-alert--error ti-screen-only">{error}</div>}

        <div className="ti-callout ti-callout--warn ti-screen-only">
          <strong>Heads up:</strong> Replacing the list deletes the current terrorist-list records, imports the new PDF, and then scans only customers with current active tickets for matching names.
        </div>

        <div className="ti-actions ti-screen-only">
          <button
            className="ti-btn ti-btn--ghost"
            onClick={() => navigate(-1)}
            disabled={uploading || rescanning}
          >
            Cancel
          </button>
          <button
            className="ti-btn ti-btn--rescan"
            type="button"
            onClick={onRescan}
            disabled={uploading || rescanning || statusLoading || (status.activeCount ?? 0) === 0}
            title="Scan the currently uploaded terrorist list against customers with active tickets"
          >
            {rescanning ? "Rescanning…" : "Rescan"}
          </button>
          <button
            className="ti-btn ti-btn--primary"
            disabled={!file || uploading || rescanning}
            onClick={() => setConfirming(true)}
          >
            {uploading ? "Importing & Scanning…" : "Replace List & Scan DB"}
          </button>
        </div>

        {confirming && (
          <div className="ti-modal-backdrop ti-screen-only">
            <div className="ti-modal">
              <h3>Confirm Replace & Scan</h3>
              <p>
                This will <b>delete all current terrorist-list entries</b>, import
                <br />
                <code>{file?.name}</code>, then scan customers with current active tickets for matches.
              </p>
              <div className="ti-modal-actions">
                <button className="ti-btn" onClick={() => setConfirming(false)} disabled={uploading}>
                  No, go back
                </button>
                <button className="ti-btn ti-btn--danger" onClick={onUpload} disabled={uploading}>
                  {uploading ? "Working…" : "Yes, replace & scan"}
                </button>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="ti-card ti-screen-only">
            <h3>{resultKind === "rescan" ? "Rescan Summary" : "Import Summary"}</h3>

            {resultKind === "upload" && (
              <table className="ti-summary">
                <tbody>
                  <tr><td>Previous list entries</td><td>{result.previousActive}</td></tr>
                  <tr><td>Imported list entries</td><td>{result.newActive}</td></tr>
                  <tr><td>Added</td><td>{result.added}</td></tr>
                  <tr><td>Removed</td><td>{result.removed}</td></tr>
                  <tr><td>Unchanged</td><td>{result.unchanged}</td></tr>
                  <tr className={matchCount > 0 ? "ti-summary-match" : ""}>
                    <td>Active-ticket customers matched</td><td>{matchCount}</td>
                  </tr>
                </tbody>
              </table>
            )}

            {resultKind === "rescan" && (
              <table className="ti-summary">
                <tbody>
                  <tr><td>Scan completed</td><td>{displayTimestamp(result.lastScanAt)}</td></tr>
                  <tr className={matchCount > 0 ? "ti-summary-match" : ""}>
                    <td>Active-ticket customers matched</td><td>{matchCount}</td>
                  </tr>
                </tbody>
              </table>
            )}

            {matchCount === 0 ? (
              <div className="ti-alert ti-alert--success">
                Scan completed. No customers with active tickets matched the current terrorist list.
              </div>
            ) : (
              <div className="ti-alert ti-alert--danger">
                <strong>Attention:</strong> {matchCount} customer{matchCount === 1 ? "" : "s"} with active ticket{matchCount === 1 ? "" : "s"} matched the terrorist list by name. Review the active ticket number(s) below. Rows marked <b>NAME + DOB</b> are stronger matches.
              </div>
            )}

            {resultKind === "upload" && Array.isArray(result.rejected) && result.rejected.length > 0 && (
              <>
                <h4>Rejected rows</h4>
                <pre className="ti-pre">
                  {result.rejected.slice(0, 200).join("\n")}
                  {result.rejected.length > 200 ? "\n…(truncated)" : ""}
                </pre>
              </>
            )}
          </div>
        )}

        {matches.length > 0 && (
          <div className="ti-card ti-match-card">
            <div className="ti-match-header">
              <div>
                <h3>Terrorist List — Active Ticket Customer Matches</h3>
                <p>
                  List updated: <strong>{displayTimestamp(result?.lastUpdatedAt || status.lastUpdatedAt)}</strong>
                  {printTime && <span className="ti-print-generated"> · Printed: {displayTimestamp(printTime)}</span>}
                </p>
              </div>
              <button className="ti-btn ti-btn--primary ti-print-hide" type="button" onClick={printMatches}>
                Print Match List
              </button>
            </div>

            <div className="ti-match-note">
              Only customers with current active, unredeemed tickets are included. Exact normalized name matching is used. <b>NAME ONLY</b> is included even when DOB differs or is unavailable; <b>NAME + DOB</b> means both fields matched.
            </div>

            <div className="ti-table-wrap">
              <table className="ti-match-table">
                <thead>
                  <tr>
                    <th>Match</th>
                    <th>Customer NRIC / ID</th>
                    <th>Customer Name</th>
                    <th>Customer DOB</th>
                    <th>Mobile</th>
                    <th>Active Ticket No(s)</th>
                    <th>Listed Name</th>
                    <th>Listed DOB</th>
                    <th>Passport</th>
                    <th>Nationality</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m, idx) => (
                    <tr key={`${m.customerNric || "customer"}-${m.terroristSn || idx}-${idx}`}>
                      <td><span className={`ti-match-badge ${m.dobMatch ? "ti-match-badge--strong" : ""}`}>{text(m.matchType)}</span></td>
                      <td>{text(m.customerNric)}</td>
                      <td>{text(m.customerName)}</td>
                      <td>{displayDate(m.customerDob)}</td>
                      <td>{text(m.customerMobile)}</td>
                      <td className="ti-ticket-list">
                        {ticketList(m).length > 0
                          ? ticketList(m).map((ticket) => <div key={ticket}>{ticket}</div>)
                          : "—"}
                      </td>
                      <td>{text(m.terroristName)}</td>
                      <td>{displayDate(m.terroristDob)}</td>
                      <td>{text(m.terroristPassportNo)}</td>
                      <td>{text(m.terroristNationality)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
